import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { DownArrowIcon } from '../../../components/icon/icon';
import { useHistory } from 'react-router-dom';
import { CommonBoard } from '../../../components/common/common.board';
import { JobSearchCondition } from '../../../components/common/common.job.search.condition';
import { fetchInquiryList } from '../../../api/inquiry';

interface SearchConditionProps {
  startDate: string;
  endDate: string;
  isDefaultRange?: boolean; // 기본 범위 여부 추가
}

interface InquiryListResponseData {
  inquiryId: number;
  title: string;
  createdAt: string;
  state: string;
}

const Inquiry = () => {
  const [loading, setLoading] = useState(true); // 로딩 상태 관리
  const [inquiryList, setInquiryList] = useState<InquiryListResponseData[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const history = useHistory();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCondition, setSearchCondition] = useState<SearchConditionProps>({
    startDate: '',
    endDate: '',
    isDefaultRange: true, // 기본값을 true로 설정
  });

  const handleSearchClick = () => {
    setSearchOpen(true);
  };
  const handleSearchClose = () => {
    setSearchOpen(false);
  };

  const handleSearchSubmit = ({
    startDate,
    endDate,
    isDefaultRange,
  }: SearchConditionProps) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setSearchCondition({ startDate, endDate, isDefaultRange }); // 상태 업데이트
    setSearchOpen(false);
  };

  const registBtnClickHandler = () => {
    history.push('/inquiry/regist');
  };

  useEffect(() => {
    const getInquiryData = async () => {
      try {
        setLoading(true);

        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // startDate와 endDate가 없을 경우 기본값 설정
        const startDateString = startDate
          ? new Date(startDate)
          : new Date('2020-01-01');
        const endDateString = endDate
          ? new Date(endDate)
          : new Date('2222-12-31');

        const data = await fetchInquiryList({
          startDate: formatDate(startDateString),
          endDate: formatDate(endDateString),
        });

        setInquiryList(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getInquiryData();
  }, [startDate, endDate]);

  if (loading) return <p>Loading...</p>;

  if (!inquiryList || inquiryList.length === 0) {
    return (
      <Container>
        <SearchForm onClick={handleSearchClick}>
          <span>
            {searchCondition.isDefaultRange
              ? '전체'
              : `${searchCondition.startDate} ~ ${searchCondition.endDate}`}
          </span>
          <div className="icon-box">
            <DownArrowIcon />
          </div>
        </SearchForm>
        <div className="no-data">검색 결과가 없습니다.</div>
        <CommonBoard
          items={inquiryList}
          registBtn={registBtnClickHandler}
          detailPathUrl="/inquiry/detail"
        />
        <AnimatePresence initial={false}>
          {searchOpen ? (
            <SearchConditionWrap
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'fade',
              }}
            >
              <JobSearchCondition
                handleSearchClose={handleSearchClose}
                handleSearchSubmit={handleSearchSubmit}
              />
            </SearchConditionWrap>
          ) : null}
        </AnimatePresence>
      </Container>
    ); // 데이터가 없을 때 표시
  }

  return (
    <Container>
      <SearchForm onClick={handleSearchClick}>
        <span>
          {searchCondition.isDefaultRange
            ? '전체'
            : `${searchCondition.startDate} ~ ${searchCondition.endDate}`}
        </span>
        <div className="icon-box">
          <DownArrowIcon />
        </div>
      </SearchForm>
      <CommonBoard
        items={inquiryList}
        registBtn={registBtnClickHandler}
        detailPathUrl="/inquiry/detail"
      />
      <AnimatePresence initial={false}>
        {searchOpen ? (
          <SearchConditionWrap
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: 'fade',
            }}
          >
            <JobSearchCondition
              handleSearchClose={handleSearchClose}
              handleSearchSubmit={handleSearchSubmit}
            />
          </SearchConditionWrap>
        ) : null}
      </AnimatePresence>
    </Container>
  );
};

export default Inquiry;

// style
const Container = styled.div`
  .no-data {
    margin-top: 2rem;
    font-size: 1.6rem;
    text-align: center;
  }
`;

const SearchForm = styled.div`
  border-bottom: 1px solid #f0f0f0;
  background-color: ${(props) => props.theme.textWhiteColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  padding: 0.3rem 0 0.3rem 2.4rem;
  span {
    padding: 1.3rem 0;
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3rem;
    color: ${(props) => props.theme.textBlackColor};
  }
  .icon-box {
    display: flex;
    padding: 1.65rem;
  }
`;

const SearchConditionWrap = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
  cursor: 'pointer';
  width: 100%;
  height: 100%;
  .overlay {
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;
